import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SelectField } from "../components/ui/select-field.js";
import { api, getAccessToken, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useLookup } from "../context/LookupContext.js";
import { DatePicker } from "../components/DatePicker.js";
import { AsyncButton } from "../components/ui/async-button.js";

import { HR_ROLES } from "@hrms/types";
import { X, CheckCircle2, Square } from "lucide-react";
import { usePageBack } from "../context/PageBackContext.js";

function decodeToken(token: string): { role: string; sub: string } {
  try {
    const p = JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>;
    return { role: String(p["role"] ?? ""), sub: String(p["sub"] ?? "") };
  } catch { return { role: "", sub: "" }; }
}

interface ChecklistItem {
  id: string; title: string; assignedTo: string | null; dueDate: string | null;
  completedAt: string | null; isMandatory: boolean; notes: string | null;
}
interface OffboardingRecord {
  id: string; lastWorkingDate: string; resignationDate: string | null; reason: string;
  exitInterviewDone: boolean; knowledgeTransferDone: boolean;
  finalPayrollSettled: boolean; accessRevoked: boolean;
  status: string; completedAt: string | null; notes: string | null;
  employee: { id: string; firstName: string; middleName?: string | null; lastName: string; employeeCode: string; status: string };
  items?: ChecklistItem[];
  _count?: { items: number };
}

const statusBadge = (s: string): React.CSSProperties => {
  if (s === "COMPLETED") return { background: "var(--color-success-bg)", color: "var(--color-success-fg)" };
  return { background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" };
};

const inputStyle: React.CSSProperties = {
  padding: "8px 10px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: "14px", background: "var(--color-surface-0)", width: "100%",
};

export default function OffboardingPage() {
  usePageBack("/dashboard");
  const navigate = useNavigate();
  const offboardingReasons = useLookup("OFFBOARDING_REASON");
  const accessToken = getAccessToken() ?? "";
  const { role, sub } = decodeToken(accessToken);
  const isHR = HR_ROLES.has(role);

  const [records, setRecords] = useState<OffboardingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<OffboardingRecord | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ employeeId: "", lastWorkingDate: "", resignationDate: "", reason: "RESIGNATION", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    try {
      if (isHR) {
        const res = await api.get<OffboardingRecord[]>("/offboarding");
        setRecords(res.data);
      } else {
        const res = await api.get<OffboardingRecord>(`/offboarding/${sub}`);
        setRecords([res.data]);
        setSelected(res.data);
      }
    } catch (err) { logError("OffboardingPage.load", err); } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function openRecord(employeeId: string) {
    setDetailLoading(true);
    try {
      const res = await api.get<OffboardingRecord>(`/offboarding/${employeeId}`);
      setSelected(res.data);
    } catch (err) { logError("OffboardingPage.openRecord", err); } finally { setDetailLoading(false); }
  }

  async function create() {
    setSaving(true); setError("");
    try {
      await api.post("/offboarding", { ...form, resignationDate: form.resignationDate || undefined, notes: form.notes || undefined });
      setShowForm(false); setForm({ employeeId: "", lastWorkingDate: "", resignationDate: "", reason: "RESIGNATION", notes: "" });
      load();
    } catch (e: unknown) {
      setError(errMsg(e) ?? "Failed");
      throw e;
    } finally { setSaving(false); }
  }

  async function updateFlag(employeeId: string, field: string, value: boolean) {
    try {
      await api.patch(`/offboarding/${employeeId}`, { [field]: value });
      openRecord(employeeId); load();
    } catch (e: unknown) {
      alert(errMsg(e) ?? "Failed");
    }
  }

  async function completeItem(itemId: string, done: boolean) {
    try {
      await api.patch(`/offboarding/items/${itemId}`, { completedAt: done ? new Date().toISOString() : null });
      if (selected) openRecord(selected.employee.id);
    } catch (e: unknown) {
      alert(errMsg(e) ?? "Failed");
    }
  }

  async function completeOffboarding(employeeId: string) {
    try {
      await api.patch(`/offboarding/${employeeId}/complete`);
      load(); openRecord(employeeId);
    } catch (e: unknown) {
      alert(errMsg(e) ?? "Failed");
      throw e;
    }
  }

  const progressBars = (r: OffboardingRecord) => [
    { label: "Exit Interview", done: r.exitInterviewDone, field: "exitInterviewDone" },
    { label: "Knowledge Transfer", done: r.knowledgeTransferDone, field: "knowledgeTransferDone" },
    { label: "Final Payroll", done: r.finalPayrollSettled, field: "finalPayrollSettled" },
    { label: "Access Revoked", done: r.accessRevoked, field: "accessRevoked" },
  ];

  return (
    <div style={{ padding: "var(--space-8)", maxWidth: 1200 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--color-text-900)" }}>Offboarding</h1>
        {isHR && (
          <button onClick={() => { setShowForm(true); setError(""); }}
            style={{ padding: "8px 16px", background: "var(--color-brand-600)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
            + Initiate Offboarding
          </button>
        )}
      </div>

      {showForm && (
        <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 20, marginBottom: 20 }}>
          <h3 style={{ fontWeight: 600, fontSize: 15, marginBottom: 16 }}>Initiate Offboarding</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Employee ID *</label>
              <input style={inputStyle} value={form.employeeId} onChange={e => setForm(f => ({ ...f, employeeId: e.target.value }))} placeholder="cuid..." />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Resignation Date</label>
              <DatePicker style={inputStyle} value={form.resignationDate} onChange={v => setForm(f => ({ ...f, resignationDate: v }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Last Working Date *</label>
              <DatePicker style={inputStyle} value={form.lastWorkingDate} onChange={v => setForm(f => ({ ...f, lastWorkingDate: v }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Reason</label>
              <SelectField value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}>
                {offboardingReasons.map(o => <option key={o.value} value={o.value}>{o.displayName}</option>)}
              </SelectField>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Notes</label>
              <input style={inputStyle} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes…" />
            </div>
          </div>
          {error && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 8 }}>{error}</p>}
          <div style={{ display: "flex", gap: 8 }}>
            <AsyncButton onClick={() => create()} disabled={saving || !form.employeeId || !form.lastWorkingDate}>
              Initiate
            </AsyncButton>
            <button onClick={() => setShowForm(false)} style={{ padding: "7px 16px", background: "transparent", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: selected && isHR ? "340px 1fr" : "1fr", gap: 20 }}>
        {isHR && (
          <div>
            {loading ? <p style={{ color: "var(--color-text-400)", fontSize: 14 }}>Loading…</p> : records.length === 0 ? (
              <p style={{ color: "var(--color-text-400)", fontSize: 14 }}>No offboarding in progress.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {records.map(r => (
                  <div key={r.id} onClick={() => openRecord(r.employee.id)} style={{
                    padding: 14, background: "var(--color-surface-0)",
                    border: selected?.id === r.id ? "2px solid var(--color-brand-500)" : "1px solid var(--color-surface-200)",
                    borderRadius: "var(--radius-lg)", cursor: "pointer",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, cursor: "pointer" }} onClick={e => { e.stopPropagation(); navigate(`/employees/${r.employee.id}/edit`); }} onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")} onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}>{[r.employee.firstName, r.employee.middleName, r.employee.lastName].filter(Boolean).join(" ")}</div>
                        <div style={{ fontSize: 12, color: "var(--color-text-500)", marginTop: 2 }}>{r.employee.employeeCode} · {r.reason}</div>
                        <div style={{ fontSize: 12, color: "var(--color-text-400)", marginTop: 2 }}>Last day: {r.lastWorkingDate.slice(0, 10)}</div>
                      </div>
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, fontWeight: 600, ...statusBadge(r.status) }}>{r.status.replace("_", " ")}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {selected && (
          <div>
            {detailLoading ? <p style={{ color: "var(--color-text-400)", fontSize: 14 }}>Loading…</p> : (
              <>
                <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 20, marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                    <div>
                      <h3 style={{ fontWeight: 700, fontSize: 16, cursor: "pointer" }} onClick={() => navigate(`/employees/${selected.employee.id}/edit`)} onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")} onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}>{[selected.employee.firstName, selected.employee.middleName, selected.employee.lastName].filter(Boolean).join(" ")}</h3>
                      <div style={{ fontSize: 13, color: "var(--color-text-500)", marginTop: 2 }}>
                        {selected.employee.employeeCode}
                        {selected.resignationDate && <> · Resigned: <strong>{selected.resignationDate.slice(0, 10)}</strong></>}
                        {" "}· Last day: <strong>{selected.lastWorkingDate.slice(0, 10)}</strong> · {selected.reason}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ fontSize: 11, padding: "2px 10px", borderRadius: 999, fontWeight: 600, ...statusBadge(selected.status) }}>{selected.status.replace("_", " ")}</span>
                      {isHR && <button onClick={() => setSelected(null)} style={{ display: "inline-flex", alignItems: "center", background: "none", border: "none", cursor: "pointer", color: "var(--color-text-400)" }}><X size={16} /></button>}
                    </div>
                  </div>

                  <h4 style={{ fontWeight: 600, fontSize: 13, color: "var(--color-text-500)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>Key Gates</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                    {progressBars(selected).map(({ label, done, field }) => (
                      <div key={field} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: done ? "var(--color-success-bg-subtle)" : "var(--color-surface-50)", border: `1px solid ${done ? "var(--color-success-border)" : "var(--color-surface-200)"}`, borderRadius: "var(--radius-md)" }}>
                        <span style={{ display: "inline-flex", color: done ? "var(--color-success)" : "var(--color-surface-400)" }}>{done ? <CheckCircle2 size={18} /> : <Square size={18} />}</span>
                        <span style={{ fontSize: 13, flex: 1, fontWeight: done ? 600 : 400, color: done ? "var(--color-success-fg)" : "var(--color-text-700)" }}>{label}</span>
                        {isHR && selected.status === "IN_PROGRESS" && (
                          <button onClick={() => updateFlag(selected.employee.id, field, !done)}
                            style={{ padding: "3px 8px", fontSize: 11, background: "transparent", border: "1px solid var(--color-surface-300)", borderRadius: "var(--radius-sm)", cursor: "pointer" }}>
                            {done ? "Undo" : "Done"}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {isHR && selected.status === "IN_PROGRESS" && (
                    <AsyncButton
                      variant="success"
                      onClick={() => (window.confirm("Complete offboarding and set employee status to TERMINATED?") ? completeOffboarding(selected.employee.id) : undefined)}
                    >
                      Complete Offboarding & Terminate Employee
                    </AsyncButton>
                  )}
                  {selected.status === "COMPLETED" && selected.completedAt && (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, color: "var(--color-success-fg)", fontWeight: 600 }}>
                      <CheckCircle2 size={14} /> Offboarding completed on {new Date(selected.completedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {selected.items && (
                  <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 20 }}>
                    <h4 style={{ fontWeight: 600, fontSize: 13, color: "var(--color-text-500)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>
                      Checklist ({selected.items.filter(i => i.completedAt).length}/{selected.items.length} done)
                    </h4>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {selected.items.map(item => (
                        <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: item.completedAt ? "var(--color-success-bg-subtle)" : "var(--color-surface-50)", border: `1px solid ${item.completedAt ? "var(--color-success-border)" : "var(--color-surface-200)"}`, borderRadius: "var(--radius-md)" }}>
                          <span style={{ display: "inline-flex", flexShrink: 0, color: item.completedAt ? "var(--color-success)" : "var(--color-surface-400)" }}>{item.completedAt ? <CheckCircle2 size={18} /> : <Square size={18} />}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: item.isMandatory ? 600 : 400, color: item.completedAt ? "var(--color-success-fg)" : "var(--color-text-700)" }}>
                              {item.title}
                              {item.isMandatory && <span style={{ fontSize: 10, marginLeft: 6, color: "var(--color-error)" }}>REQUIRED</span>}
                            </div>
                            {item.completedAt && <div style={{ fontSize: 11, color: "var(--color-text-400)" }}>Completed {new Date(item.completedAt).toLocaleDateString()}</div>}
                          </div>
                          {isHR && selected.status === "IN_PROGRESS" && (
                            <button onClick={() => completeItem(item.id, !item.completedAt)}
                              style={{ padding: "3px 8px", fontSize: 11, background: "transparent", border: "1px solid var(--color-surface-300)", borderRadius: "var(--radius-sm)", cursor: "pointer", flexShrink: 0 }}>
                              {item.completedAt ? "Undo" : "Mark Done"}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {sub && <span style={{ display: "none" }}>{sub}</span>}
    </div>
  );
}
